import React, { useEffect, useState } from 'react';
import Button from '../../components/common/Button/Button';
import Modal from '../../components/common/Modal/Modal';
import InputField from '../../components/common/InputField/InputField';
import styles from '../AccountHead/AccountHeadList.module.scss';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { accountHeadService } from '../../services/accountHeadService';

// Helper to format date to 'YYYY-MM-DD HH:mm:ss'
function formatDate(dateString: string) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const pad = (n: number) => n.toString().padStart(2, '0');
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

const AccountHeadList: React.FC = () => {
  const [accountHeads, setAccountHeads] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({ name: '', is_active: true, is_des: false });
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch account heads
  const fetchAccountHeads = async () => {
    setLoading(true);
    try {
      const data = await accountHeadService.getAccountHeads();
      setAccountHeads(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to fetch account heads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccountHeads();
  }, []);

  const handleOpenModal = () => {
    setForm({ name: '', is_active: true, is_des: false });
    setEditMode(false);
    setEditingId(null);
    setShowModal(true);
  };

  const handleEdit = (ah: any) => {
    setForm({ name: ah.name, is_active: ah.is_active, is_des: ah.is_des });
    setEditMode(true);
    setEditingId(ah.id);
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this account head?')) return;
    try {
      await accountHeadService.deleteAccountHead(id);
      toast.success('Deleted successfully');
      fetchAccountHeads();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleToggleActive = async (ah: any) => {
    try {
      await accountHeadService.updateAccountHead(ah.id, { ...ah, is_active: !ah.is_active });
      fetchAccountHeads();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    try {
      if (editMode && editingId) {
        await accountHeadService.updateAccountHead(editingId, form);
        toast.success('Updated successfully');
      } else {
        await accountHeadService.createAccountHead(form);
        toast.success('Created successfully');
      }
      setShowModal(false);
      fetchAccountHeads();
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save');
    }
  };

  const filtered = accountHeads.filter((ah: any) => {
    return (
      (ah.name?.toLowerCase() || '').includes(searchTerm.toLowerCase())
    );
  });

  return (
    <div className={styles.container}>
      <h2>Account Heads</h2>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.searchContainer}>
            <div className={styles.searchWrapper}>
              <svg className={styles.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                placeholder="Search by name or created by..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
              {searchTerm && (
                <button
                  className={styles.clearButton}
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear search"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
        <div className={styles.headerRight}>
          <Button variant="primary" onClick={handleOpenModal}>
            + Add Account Head
          </Button>
        </div>
      </div>
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Created By</th>
              <th>Created At</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center', padding: '20px' }}>
                  Loading...
                </td>
              </tr>
            ) : filtered.length > 0 ? (
              filtered.map((ah: any) => {
                return (
                  <tr key={ah.id}>
                    <td>{ah.name}</td>
                    <td>{ah.created_by || '-'}</td>
                    <td>{formatDate(ah.created_at)}</td>
                    <td>
                      <span
                        style={{ cursor: 'pointer' }}
                        title={ah.is_active ? 'Active' : 'Inactive'}
                        onClick={() => handleToggleActive(ah)}
                      >
                        {ah.is_active ? (
                          // Active SVG
                          <svg width="20" height="20" fill="none" stroke="#059669" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M9 12l2 2 4-4"/></svg>
                        ) : (
                          // Inactive SVG
                          <svg width="20" height="20" fill="none" stroke="#b91c1c" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="8" y1="8" x2="16" y2="16"/><line x1="16" y1="8" x2="8" y2="16"/></svg>
                        )}
                      </span>
                    </td>
                    <td>
                    <div className={styles.actions}>
                      <Button
                        className={styles.actionButton}
                        onClick={() => handleEdit(ah)}
                        aria-label="Edit"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19.5 3 21l1.5-4L16.5 3.5z"/></svg>
                      </Button>
                      <Button
                        className={styles.actionButton}
                        onClick={() => handleDelete(ah.id)}
                        aria-label="Delete"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                      </Button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center', padding: '20px' }}>
                  No account heads found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editMode ? 'Edit Account Head' : 'Add Account Head'}>
        <div className={styles.modalForm}>
          <label htmlFor="accountHeadName" style={{ marginBottom: 4 }}>Name</label>
          <InputField
            name="name"
            type="text"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            placeholder="Account Head Name"
            required
          />
          {/* <div className={styles.toggleRow}>
          <label className={styles.toggleLabel}>
              <span>Show Description</span>
              <label className={styles.toggleSwitch}>
                <input
                  type="checkbox"
                  checked={form.is_des}
                  onChange={e => setForm({ ...form, is_des: e.target.checked })}
                />
                <span className={styles.slider}></span>
              </label>
            </label>
            </div> */}
          <div style={{ marginTop: 16 }}>
            <Button variant="primary" onClick={handleSave}>
              {editMode ? 'Save Changes' : 'Add'}
            </Button>
          </div>
        </div>
      </Modal>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default AccountHeadList; 